import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  BreakpointObserverService,
  HREF,
  LocalStorageKeyEnum,
  LocalStorageService,
  NavigationService,
  ProductService,
} from '@core';
import { Observable, of } from 'rxjs';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  pluck,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ProductSearch } from '@models';
import { SkeletonTemplateService } from '@skeleton-loader';
import { AlertMessageComponent } from '../alert-message/alert-message.component';
import { AccessibleClickDirective } from '../directives/accessible-click.directive';
import { Router } from '@angular/router';

@Component({
  selector: 'lib-search-panel',
  templateUrl: './search-panel.component.html',
  styleUrl: './search-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    AlertMessageComponent,
    AccessibleClickDirective,
  ],
})
export class SearchPanelComponent implements OnInit {
  private productService = inject(ProductService);
  private destroyRef = inject(DestroyRef);
  private localStorageService = inject(LocalStorageService);
  readonly skeletonService = inject(SkeletonTemplateService);
  private navigationService = inject(NavigationService);
  private router = inject(Router);

  readonly isMobile = inject(BreakpointObserverService).isMobile;
  readonly close = output<void>();

  readonly searchField = viewChild<ElementRef>('searchField');

  form = new FormGroup({
    query: new FormControl<string>(''),
  });

  isLoading = signal(false);
  searchResult = signal<null | ProductSearch[]>(null);
  isEmptySearchString = toSignal(
    this.getQueryChangesObservable().pipe(
      startWith(this.form.value.query),
      map((query) => !query?.length),
    ),
  );
  recentSearches = signal<string[]>(this.localStorageService.getItem(LocalStorageKeyEnum.RecentSearches));

  public focus() {
    // Used of and delay, because lifecycle's hooks init triggers only once, and checked LC hooks triggers on each changes.
    // Autofocus and default focus doesn't work without delay
    of(this.searchField())
      .pipe(delay(100))
      .subscribe((ref) => {
        ref?.nativeElement.focus();
      });
  }

  ngOnInit(): void {
    this.bindSearchQueryChangeToDataFetch();
    this.focus();
  }

  clearQuery(clickEvent: Event): void {
    clickEvent.stopPropagation();

    this.form.controls.query?.setValue('');
    this.searchResult.set(null);
    this.searchField()?.nativeElement.focus();
  }

  removeSearchResult(query: string, clickEvent: Event): void {
    clickEvent.stopPropagation();
    const updated = this.recentSearches().filter((v) => v !== query);
    this.localStorageService.setItem(LocalStorageKeyEnum.RecentSearches, updated);
    this.recentSearches.set(updated);
  }

  applySearchQuery(query: string): void {
    this.form.controls.query.patchValue(query);
  }

  goToProduct(item: ProductSearch): void {
    this.navigationService.goToProductPage(item.productId, item.brandId);

    this.form.controls.query.reset('');
    this.searchResult.set(null);
    this.close.emit();
  }

  private bindSearchQueryChangeToDataFetch(): void {
    this.getQueryChangesObservable()
      .pipe(
        tap((query) => this.saveSearchToStorage(query)),
        tap(() => this.isLoading.set(true)),
        switchMap((query) => (query.length ? this.productService.search({ keyword: query }) : of(null))),
        tap((result) => {
          this.searchResult.set(result?.items || []);
          this.isLoading.set(false);
        }),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getQueryChangesObservable(): Observable<string> {
    return this.form.valueChanges.pipe(
      debounceTime(400),
      pluck('query'),
      filter((query) => typeof query === 'string'),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  saveSearchToStorage(item: string): void {
    if (item.length === 0) return;
    const items: string[] = this.localStorageService.getItem(LocalStorageKeyEnum.RecentSearches) || [];
    const filteredItems = items?.filter((v: string) => v.toLowerCase() !== item.toLowerCase());
    filteredItems.unshift(item);
    const limitedSearches = filteredItems.slice(0, 8);
    this.localStorageService.setItem(LocalStorageKeyEnum.RecentSearches, limitedSearches);
    this.recentSearches.set(limitedSearches);
  }

  onRedirect(): void {
    const value = this.form.value.query;
    if (!value) return;
    this.close.emit();
    this.form.controls.query.reset('');
    this.router.navigate([HREF.SEARCH_RESULTS, value]);
  }
}
